import React, { Fragment, Component } from "react";
import styled from "styled-components";
import Helmet from "react-helmet";
import PropTypes from "prop-types";
import Img from "gatsby-image";
import Flex, { FlexItem } from "styled-flex-component";
import { Title, SubTitle, CenteredFull } from "../components/Typography";
import Tithely from "../components/Tithely";
import { OutboundLink } from "gatsby-plugin-google-analytics";
import { DonateButton as _DonateButton } from "../components/DonateRow";

export default class DonatePage extends Component {
	static propTypes = {
		data: PropTypes.shape({
			markdownRemark: PropTypes.shape({
				frontmatter: PropTypes.shape({
					title: PropTypes.string
				})
			})
		})
	};

	render() {
		const { markdownRemark: post } = this.props.data;
		return (
			<Fragment>
				<Helmet title="Donate" />
				<DonateContainer>
					<DonateTitleContainer>
						<Title lineheight="0">{post.frontmatter.title}</Title>
						<SubTitle>Sponsor a Transformation Today!</SubTitle>
					</DonateTitleContainer>
					<DonatePercentContainer inline alignCenter>
						<DonatePercentage>100%</DonatePercentage>
						<h3>
							of the funds given go towards
							<br />
							supporting our work.
						</h3>
					</DonatePercentContainer>
					<PercentageTagline>
						At Narrow Road Home, we believe in the power of partnership. We rely on the generous support of benefactors
						to continue our mission of healing.
					</PercentageTagline>
					<FundsContainer>
						<FundsTitle>Where Do Funds Go?</FundsTitle>
						<FundsList>
							<FundsListItem>
								<h6>Groceries & House Supplies</h6>
								<i>Support the Narrow Road Home with daily costs to assist women on their recovery journey.</i>
							</FundsListItem>
							<FundsListItem>
								<h6>Assisting Women with Recovery Costs</h6>
								<i>
									The cost to house and assist women in their journey is constantly evolving. We try our best to help
									women regardless of their financial situation, and rely on your help to do so.
								</i>
							</FundsListItem>
							<FundsListItem>
								<h6>Narrow Road Home Growth Fund</h6>
								<i>This fund allows us to further our reach to connect with women who need help.</i>
							</FundsListItem>
						</FundsList>
					</FundsContainer>
					<DonateButtonContainer>
						<Tithely />
					</DonateButtonContainer>
					<DonateButtonContainer>
						<OutboundLink
							target="_blank"
							style={{ color: "white" }}
							href="https://gcfcanada.com/narrow-road-home-project/">
							<DonateButton>
								Alternate Donation*
								<br />
								<small>with the Great Commission Foundation</small>
							</DonateButton>
						</OutboundLink>
					</DonateButtonContainer>
					<DonateDetailsContainer>
						*Donations can be made though 'The Great Commission Foundation' and are applicable for tax receipts. This
						button will redirect you to The Great Commission Foundation's website. Under the field 'designation' please
						select 'narrow road home' from the dropdown menu. Thank you for your support and partnership!
					</DonateDetailsContainer>
				</DonateContainer>
			</Fragment>
		);
	}
}

const DonateDetailsContainer = styled.div`
	padding: 0 ${100 / 24}vw ${100 / 24}vw 0;
`;

const DonateButton = styled.span`
	background: ${props => props.theme.color.lightGray};
	padding: 1rem 2rem;
	font-size: 1rem;
	font-weight: 500;
	text-transform: uppercase;
	display: inline-block;
	border-radius: 10px;
	color: black;
	transition: ${props => props.theme.animation.normal};

	small {
		font-weight: lighter;
		font-size: 0.8rem !important;
	}

	&:hover {
		background: ${props => props.theme.color.gray};
	}
`;

const DonateButtonContainer = styled.div`
	margin-top: ${100 / 24}vw;
	margin-bottom: ${100 / 24 / 2}vw;

	@media screen and (max-width: 800px) {
		margin: ${(100 / 24) * 3}vw 0;
	}
`;

const DonateContainer = styled.div`
	padding-left: ${(100 / 24) * 3}vw;
`;

const DonateTitleContainer = styled.div`
	margin-top: ${(100 / 24) * 2}vw;

	@media screen and (max-width: 800px) {
		margin-top: ${(100 / 24) * 4}vw;

		${Title} {
			max-width: 100%;
		}

		${SubTitle} {
			font-size: 1.25rem !important;
			max-width: 80%;
		}
	}
`;

const DonatePercentContainer = Flex.extend`
	border-bottom: 5px solid ${props => props.theme.color.green};
	padding-top: ${100 / 24 / 2}vw;
	padding-right: ${(100 / 24) * 1.5}vw;

	@media screen and (max-width: 800px) {
		flex-wrap: wrap;
		h3 {
			font-size: 1.25rem !important;
		}
	}
`;

const DonatePercentage = styled.span`
	font-size: 4.44rem;
	font-weight: bold;
	margin-right: 2rem;
	color: ${props => props.theme.color.green};

	@media screen and (max-width: 800px) {
		font-size: 3rem;
	}
`;

const PercentageTagline = styled.div`
	margin-top: 1rem;
	max-width: 740px;
	font-weight: 500;

	@media screen and (max-width: 800px) {
		max-width: 80%;
	}
`;

const FundsContainer = styled.div`
	@media screen and (max-width: 800px) {
		margin-top: ${(100 / 24) * 3}vw;
	}
`;

const FundsTitle = styled.h1`
	margin-top: ${(100 / 24) * 1}vw;
	font-weight: normal;
	padding-left: 2rem;
	color: ${props => props.theme.color.darkGreen};
	border-left: solid 5px ${props => props.theme.color.darkGreen};

	@media screen and (max-width: 800px) {
		font-size: 1.5rem !important;
		border: none;
		padding-left: 0;
	}
`;

const FundsList = styled.ol`
	list-style-type: none;
	margin-left: 0;
	padding-left: 0;
`;

const FundsListItem = styled.li`
	counter-increment: fundslistcounter;
	margin: 1rem 0;
	max-width: ${(100 / 24) * 10}vw;

	@media screen and (max-width: 800px) {
		max-width: 80%;
	}

	h6 {
		font-size: 1.5rem;
		display: inline;
		color: ${props => props.theme.color.darkTeal};

		@media screen and (max-width: 800px) {
			font-size: 1.25rem;
		}
	}

	i {
		font-size: 1rem;
		line-height: 1.35;
		display: block;
		color: ${props => props.theme.color.darkGray};

		@media screen and (max-width: 800px) {
		}
	}

	&:first-child {
		counter-reset: fundslistcounter;
	}

	&::before {
		content: counter(fundslistcounter) " ";
		font-weight: bold;
		color: ${props => props.theme.color.darkTeal};
		display: inline-block;
		width: 2rem;
		font-size: 2rem;
		opacity: 0.85;

		@media screen and (max-width: 800px) {
			font-size: 1.5rem;
		}
	}
`;

export const donatePageQuery = graphql`
	query DonatePage($id: String!) {
		markdownRemark(id: { eq: $id }) {
			frontmatter {
				title
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/templates/donate-page.jsx